import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { deviceDisplayName } from '@/cloud/services/device-display';
import { ChangeDetectionStrategy, Component, inject, signal, effect, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceContextService, DeviceDetails } from './device-context.service';
import { ActivatedRoute, Router, RouterModule, NavigationEnd } from '@angular/router';
import { BREADCRUMB_HOME, BreadcrumbsComponent } from '@/shared/breadcrumbs/breadcrumbs.component';
import { MenubarModule } from 'primeng/menubar';
import { MenuItem, MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, from, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { finalize, catchError } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { LocationPickerComponent } from '@/cloud/components/location-picker/location-picker.component';
import { DeployVersionDialog } from './deploy-version/deploy-version-dialog';

// DeviceDetails type now provided by DeviceContextService

@Component({
    selector: 'app-device',
    standalone: true,
    imports: [CommonModule, RouterModule, BreadcrumbsComponent, MenubarModule, DialogModule, ButtonModule, LocationPickerComponent, DeployVersionDialog],
    templateUrl: './device.html',
    styleUrl: './device.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class Device {
    private static readonly CHILD_BREADCRUMB_LABELS: Record<string, string> = {
        logs: 'Logs',
        actions: 'Actions',
        messages: 'Messages',
        config: 'Config',
        autodiscovery: 'Auto Discovery',
        console: 'Remote Console',
        ping: 'Ping',
        'property-list': 'Property List',
        credentials: 'Credentials',
        appliance: 'Appliance Status',
        bacnet: 'BACnet',
        mqtt: 'MQTT',
        'file-explorer': 'File Explorer',
        firmware: 'Firmware',
        'linked-devices': 'Linked Devices',
        power: 'Power',
        'ui-control': 'UI Control'
    };

    /** Template helper — see deviceDisplayName for the precedence. */
    displayName = deviceDisplayName;

    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private ctx = inject(DeviceContextService);
    private messageService = inject(MessageService);
    // Dialog visibility signals
    unclaimDialogVisible = signal(false);
    changeLocationDialogVisible = signal(false);
    deployVersionDialogVisible = signal(false);
    selectedLocationId = signal<string | null>(null);
    changingLocation = signal(false);
    changeLocationError = signal<string | null>(null);

    // Deploying a package version is gated on the same capability the API enforces.
    canDeployPackages = computed(() => this.api.hasCapability(this.liveDeviceSig()?.appNamespaceId, 'packages.manage'));

    menuItems = computed((): MenuItem[] => {
        const isAppliance = this.liveDeviceSig()?.payload?.devicePlatform === 'Appliance';
        return [
            // Always visible (like the other tunnel-dependent pages); the UI Control page
            // itself shows an offline placeholder when the device tunnel is down.
            {
                label: 'UI Control',
                icon: 'fa-solid fa-tablet-screen-button',
                command: () => this.onGotoUiControl()
            },
            {
                label: 'Status',
                icon: 'fa-solid fa-chart-line',
                items: [
                    {
                        label: 'Diagnostic Messages',
                        icon: 'fa-solid fa-stethoscope',
                        command: () => this.onDiagnosticMessages()
                    },
                    {
                        label: 'Device Logs',
                        icon: 'fa-solid fa-list',
                        command: () => this.onDeviceLogs()
                    },
                    {
                        label: 'Auto Discovery',
                        icon: 'fa-solid fa-radar',
                        command: () => this.onGotoAutoDiscovery()
                    },
                    {
                        label: 'Linked Devices',
                        icon: 'fa-solid fa-microchip',
                        command: () => this.onLinkedDevices()
                    },
                    // Always visible; the page shows an empty state when the processor reports no PDU.
                    {
                        label: 'Power',
                        icon: 'fa-solid fa-plug-circle-bolt',
                        command: () => this.onGotoPower()
                    },
                    ...(isAppliance
                        ? [
                              {
                                  label: 'Appliance Status',
                                  icon: 'fa-solid fa-server',
                                  command: () => this.onGotoApplianceStatus()
                              },
                              {
                                  label: 'BACnet',
                                  icon: 'fa-solid fa-network-wired',
                                  command: () => this.onGotoBacnet()
                              }
                          ]
                        : []),
                    // Always visible, below BACnet; the page explains when MQTT is off on the processor.
                    {
                        label: 'MQTT',
                        icon: 'fa-solid fa-tower-broadcast',
                        command: () => this.onGotoMqtt()
                    }
                ]
            },
            {
                label: 'Manage',
                icon: 'fa-solid fa-wrench',
                items: [
                    {
                        label: 'Config',
                        icon: 'fa-solid fa-cog',
                        command: () => this.onGotoConfig()
                    },
                    {
                        label: 'Actions',
                        icon: 'fa-solid fa-bolt',
                        command: () => this.onDeviceActions()
                    },
                    ...(this.canDeployPackages()
                        ? [
                              {
                                  label: 'Update Software',
                                  icon: 'fa-solid fa-cloud-arrow-down',
                                  command: () => this.openDeployVersionDialog()
                              }
                          ]
                        : []),
                    ...(isAppliance
                        ? [
                              {
                                  label: 'Firmware Update',
                                  icon: 'fa-solid fa-microchip',
                                  command: () => this.onGotoFirmware()
                              }
                          ]
                        : []),
                    {
                        label: 'Remote Console',
                        icon: 'fa-solid fa-terminal',
                        command: () => this.onGotoConsole()
                    },
                    {
                        label: 'Ping',
                        icon: 'fa-solid fa-signal-stream',
                        command: () => this.onGotoPing()
                    },
                    {
                        label: 'Property List',
                        icon: 'fa-solid fa-list-check',
                        command: () => this.onGotoPropertyList()
                    },
                    {
                        label: 'File Explorer',
                        icon: 'fa-solid fa-folder-open',
                        command: () => this.onGotoFileExplorer()
                    },
                    {
                        label: 'Credentials',
                        icon: 'fa-solid fa-key',
                        command: () => this.onGotoCredentials()
                    }
                ]
            },
            {
                label: 'Options',
                icon: 'fa-solid fa-sliders',
                items: [
                    {
                        label: 'Change Location',
                        icon: 'fa-solid fa-map-marker-alt',
                        command: () => this.onChangeLocation()
                    },
                    {
                        label: 'Unclaim Device',
                        icon: 'fa-solid fa-unlink',
                        command: () => this.openUnclaimDialog()
                    }
                ]
            }
        ];
    });

    onBackToDevice() {
        const device = this.liveDeviceSig();
        if (device) {
            this.router.navigate(['/device', device.id]);
        }
    }

    openDeployVersionDialog() {
        const device = this.liveDeviceSig();
        if (!device) {
            this.messageService.add({ severity: 'warn', summary: 'Device Not Ready', detail: 'Device not loaded yet. Please wait.' });
            return;
        }
        this.deployVersionDialogVisible.set(true);
    }

    onChangeLocation() {
        const device = this.liveDeviceSig();
        if (!device) {
            this.messageService.add({ severity: 'warn', summary: 'Device Not Ready', detail: 'Device not loaded yet. Please wait.' });
            return;
        }
        this.changeLocationError.set(null);
        this.changeLocationDialogVisible.set(true);
    }

    closeChangeLocationDialog() {
        this.changeLocationDialogVisible.set(false);
        this.selectedLocationId.set(null);
        this.changeLocationError.set(null);
    }

    onLocationSelected(locationId: string | null) {
        this.selectedLocationId.set(locationId);
    }

    confirmChangeLocation() {
        const device = this.liveDeviceSig();
        if (!device) {
            this.closeChangeLocationDialog();
            return;
        }
        const newLocationId = this.selectedLocationId();
        if (newLocationId === device.locationNodeId) {
            this.closeChangeLocationDialog();
            return;
        }
        this.changingLocation.set(true);
        this.changeLocationError.set(null);
        this.api
            .updateDeviceLocation(device.id, newLocationId)
            .then((res) => {
                // Refresh device data
                this.api.getDevice(device.id).then((d) => this.api.seedDevice(d));
                this.closeChangeLocationDialog();
            })
            .catch((err) => {
                const msg = typeof err === 'string' ? err : err?.error || err?.message || 'Failed to change location';
                this.changeLocationError.set(msg);
            })
            .finally(() => {
                this.changingLocation.set(false);
            });
    }

    openUnclaimDialog() {
        const device = this.liveDeviceSig();
        if (!device) {
            this.messageService.add({ severity: 'warn', summary: 'Device Not Ready', detail: 'Device not loaded yet. Please wait.' });
            return;
        }
        this.unclaimDialogVisible.set(true);
    }

    closeUnclaimDialog() {
        this.unclaimDialogVisible.set(false);
    }

    confirmUnclaim() {
        const device = this.liveDeviceSig();
        if (!device) {
            this.closeUnclaimDialog();
            return;
        }
        this.loadingSig.set(true);
        this.api
            .unclaimDevice(device.id)
            .then((res) => {
                this.api.getDevice(device.id).then((d) => this.api.seedDevice(d));
                const code = res.registrationCode || '(none)';
                this.messageService.add({ severity: 'success', summary: 'Device Unclaimed', detail: `Device unclaimed successfully. New registration code: ${code}` });
            })
            .catch((err) => {
                const msg = typeof err === 'string' ? err : err?.error || err?.message || 'Failed to unclaim';
                this.messageService.add({ severity: 'error', summary: 'Unclaim Failed', detail: msg });
            })
            .finally(() => {
                this.loadingSig.set(false);
                this.closeUnclaimDialog();
            });
    }

    onDeviceLogs() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'logs']);
        }
    }

    onLinkedDevices() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'linked-devices']);
        }
    }

    onDiagnosticMessages() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'messages']);
        }
    }

    onDeviceActions() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'actions']);
        }
    }

    onGotoConfig() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'config']);
        }
    }

    onGotoUiControl() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'ui-control']);
        }
    }

    onGotoAutoDiscovery() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'autodiscovery']);
        }
    }

    onGotoConsole() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'console']);
        }
    }

    onGotoPower() {
        const device = this.liveDeviceSig();
        if (device) {
            this.router.navigate(['/device', device.id, 'power']);
        }
    }

    onGotoPing() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'ping']);
        }
    }

    onGotoPropertyList() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'property-list']);
        }
    }

    onGotoFileExplorer() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'file-explorer']);
        }
    }

    onGotoCredentials() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'credentials']);
        }
    }

    onGotoApplianceStatus() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'appliance']);
        }
    }

    onGotoBacnet() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'bacnet']);
        }
    }

    onGotoMqtt() {
        const device = this.liveDeviceSig();
        if (device) {
            this.router.navigate(['/device', device.id, 'mqtt']);
        }
    }

    onGotoDashboard() {
        this.router.navigate(['/dashboard']);
    }

    onGotoFirmware() {
        const device = this.liveDeviceSig();
        if (device && this.router && typeof this.router.navigate === 'function') {
            this.router.navigate(['/device', device.id, 'firmware']);
        }
    }

    // Internal subjects for loading/error state
    private loadingSubject = new BehaviorSubject<boolean>(false);
    private errorSubject = new BehaviorSubject<string | null>(null);
    private notFoundSubject = new BehaviorSubject<boolean>(false);

    // Exposed observables for template (async pipe)
    loading$ = this.loadingSubject.asObservable();
    error$ = this.errorSubject.asObservable();
    notFound$ = this.notFoundSubject.asObservable();

    // DeviceId stream from route params
    deviceId$ = this.route.paramMap.pipe(
        map((pm) => pm.get('deviceId')),
        filter((id): id is string => !!id),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    // Base device fetch (one-shot per id)
    baseDevice$ = this.deviceId$.pipe(
        tap(() => {
            this.loadingSubject.next(true);
            this.errorSubject.next(null);
            this.notFoundSubject.next(false);
        }),
        switchMap((id) =>
            from(this.api.getDevice(id)).pipe(
                tap((d) => {
                    if (d) this.api.seedDevice(d);
                }),
                catchError((err) => {
                    if (err?.status === 404) {
                        this.notFoundSubject.next(true);
                    } else {
                        const msg = err?.error || err?.message || 'Failed to load device';
                        this.errorSubject.next(typeof msg === 'string' ? msg : 'Failed to load device');
                    }
                    return of(null);
                }),
                finalize(() => this.loadingSubject.next(false))
            )
        ),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    // Live snapshot stream (auto-unsub when async pipe completes)
    snapshot$ = this.deviceId$.pipe(
        switchMap((id) => this.api.subscribeToDevice(id)),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    // Merged live device: combine baseline fetch with snapshots
    liveDevice$ = combineLatest([this.baseDevice$, this.snapshot$]).pipe(
        map(([base, snap]) => {
            if (snap === null) return null; // deletion tombstone
            if (!base) return snap; // snapshot before base fetch
            return { ...base, ...snap } as DeviceDetails;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    // Signals bridged manually
    loadingSig = signal(false);
    errorSig = signal<string | null>(null);
    notFoundSig = signal(false);
    liveDeviceSig = signal<DeviceDetails | null>(null);
    namespaces = this.api.namespaces;
    locations = this.api.locations;

    // Computed tree structure for location picker in device's namespace
    // Breadcrumb chain: Home -> Namespace -> Location chain -> Device
    breadcrumbItems = computed(() => {
        const device = this.liveDeviceSig();
        if (!device) return [] as { label: string; routerLink: any[] | string }[];
        const nsId = device.appNamespaceId;
        const namespaces = this.namespaces();
        const locations = this.locations();
        const items: { label: string; routerLink: any[] | string }[] = [];
        if (nsId && namespaces) {
            const ns = namespaces.find((n) => n.id === nsId);
            if (ns) {
                items.push({ label: ns.name, routerLink: ['/dashboard', ns.id] });
            }
        }
        if (nsId && locations && device.locationNodeId) {
            const locsInNs = locations.filter((l) => l.appNamespaceId === nsId);
            const map = new Map<string, (typeof locsInNs)[0]>();
            locsInNs.forEach((l) => map.set(l.id, l));
            const chain: (typeof locsInNs)[0][] = [];
            let cursor = map.get(device.locationNodeId);
            const safetyLimit = 50;
            let guard = 0;
            while (cursor && guard++ < safetyLimit) {
                chain.unshift(cursor);
                cursor = cursor.parentId ? map.get(cursor.parentId) : undefined;
            }
            chain.forEach((c) => items.push({ label: c.name, routerLink: ['/dashboard', nsId, c.id] }));
        }
        // Device itself
        items.push({ label: deviceDisplayName(device), routerLink: ['/device', device.id] });
        const child = this.currentChildPath();
        const childLabel = Device.CHILD_BREADCRUMB_LABELS[child];
        if (childLabel) {
            items.push({ label: childLabel, routerLink: ['/device', device.id, child] });
        }
        return items;
    });
    breadcrumbHome = BREADCRUMB_HOME;

    private tickTimer: any = null;
    private nowTick = signal<Date>(new Date());

    // Reactive child path segment using router events to force signal recompute
    private currentChildPathSig = signal<string>('');
    currentChildPath = computed(() => this.currentChildPathSig());

    constructor() {
        // Bridge loading - using takeUntilDestroyed for automatic cleanup
        this.loading$.pipe(takeUntilDestroyed()).subscribe((v) => this.loadingSig.set(v));
        this.error$.pipe(takeUntilDestroyed()).subscribe((v) => this.errorSig.set(v));
        this.notFound$.pipe(takeUntilDestroyed()).subscribe((v) => this.notFoundSig.set(v));
        this.liveDevice$.pipe(takeUntilDestroyed()).subscribe((v) => {
            this.liveDeviceSig.set(v);
            this.ctx.setDevice(v);
        });

        effect(() => {
            console.log('Device updated:', this.liveDeviceSig());
        });

        // Start ticking every second for live relative time updates
        this.tickTimer = setInterval(() => this.nowTick.set(new Date()), 1000);

        // Listen to router navigation end to update child path for breadcrumbs - using takeUntilDestroyed
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe(() => {
                const childPath = this.route.firstChild?.routeConfig?.path || '';
                this.currentChildPathSig.set(childPath);
            });

        // Initial set (in case firstChild already present)
        this.currentChildPathSig.set(this.route.firstChild?.routeConfig?.path || '');

        // Clean up timer on destroy using effect cleanup
        effect((onCleanup) => {
            onCleanup(() => {
                if (this.tickTimer) {
                    clearInterval(this.tickTimer);
                    this.tickTimer = null;
                }
            });
        });
    }

    // Telemetry helpers removed (moved to DeviceOverview child)
}
